import '../css/ScrollGraphic.css';
import { OPTIONS } from './ScrollGraphic.details';
import { OverlayScrollbars } from 'overlayscrollbars';

export default class ScrollGraphic {
	wrapper: HTMLElement | null;
	private canvas!: HTMLCanvasElement;
	private ctx!: CanvasRenderingContext2D;

	private noise = new Perlin(this.hash32(OPTIONS.text));

	private fontReady = false;
	private fontPxCSS = 64;

	private width = 0; // CSS px
	private height = 0; // CSS px
	private startY = 0;
	private bottomPad = 0;
	private travel = 0;
	private fontPx = 64;

	private randX: number[] = [];
	private randA: number[] = [];

	private rafId: number | null = null;
	private _onScroll = this.handleScroll.bind(this);
	private _onResize = this.handleResize.bind(this);

	constructor() {
		this.wrapper = document.getElementById('scroll-graphic');
	}

	init(): void {
		this.canvas = document.createElement('canvas');
		this.canvas.style.display = 'block';
		this.wrapper?.append(this.canvas);
		this.ctx = this.canvas.getContext('2d', { alpha: true })!;

		this.computeSize();
		this.precomputeRandoms();
		this.loadFont().then(() => this.update());

		window.addEventListener('scroll', this._onScroll, { passive: true });
		window.addEventListener('resize', this._onResize, { passive: true });
		window.addEventListener('orientationchange', this._onResize);
	}

	// ---- sizing -------------------------------------------------------------

	private primaryFont(): string {
		const fam = (OPTIONS as any).fontFamily || 'sans-serif';
		const first = String(fam).split(',')[0].trim();
		return first.replace(/^['"]|['"]$/g, ''); // strip quotes
	}

	private async loadFont(): Promise<void> {
		if (!('fonts' in document)) {
			this.fontReady = true;
			return;
		}
		const face = this.primaryFont();
		// use CSS px, not device px
		const px = Math.max(16, this.fontPxCSS || 64);
		try {
			await (document as any).fonts.load(`${px}px ${face}`);
			await (document as any).fonts.ready;
		} finally {
			this.fontReady = true;
		}
	}

	private computeSize(): void {
		this.width = window.innerWidth;
		this.height = window.innerHeight;

		const dpr = OPTIONS.dpr;
		const w = Math.max(1, Math.round(this.width * dpr));
		const h = Math.max(1, Math.round(this.height * dpr));

		for (const c of [this.canvas]) {
			if (!c) continue;
			c.width = w;
			c.height = h;
			c.style.width = this.width + 'px';
			c.style.height = this.height + 'px';
		}

		// responsive font: CSS px from breakpoints → device px for canvas
		this.fontPxCSS = this.fontPxCSSFromWidth(this.width);
		this.fontPx = Math.max(1, Math.round(this.fontPxCSS * dpr));

		// vertical travel stays height-based
		this.startY = this.height * OPTIONS.startYFactor;
		this.bottomPad = this.height * OPTIONS.bottomPadFactor;
		this.travel = Math.max(0, this.height - this.bottomPad - this.startY);
	}

	private handleResize(): void {
		this.computeSize();
		this.scheduleUpdate();
	}

	private handleScroll(): void {
		this.scheduleUpdate();
	}

	private scheduleUpdate(): void {
		if (this.rafId != null) return;
		this.rafId = requestAnimationFrame(() => {
			this.rafId = null;
			this.update();
		});
	}

	// ---- drawing ------------------------------------------------------------

	private update(): void {
		if (!this.fontReady) return;

		const dpr = OPTIONS.dpr;
		const ctx = this.ctx;

		// clear
		ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

		// scroll position (CSS px) – prefers OverlayScrollbars body instance
		const scrollYcss = this.getScrollY();

		// clamp scroll to "travel" → this is the pin/hold behaviour
		const clamped = Math.min(Math.max(scrollYcss, 0), this.travel);
		const baseYcss = this.startY + clamped; // CSS px
		const baseY = Math.round(baseYcss * dpr); // device px

		// normalized progress in [0,1] along the intro segment
		const progress = this.travel > 0 ? clamped / this.travel : 0;
		const fade = this.computeFade(progress); // 1 → 0 between 0.7–1.0

		const stepCSS = OPTIONS.step; // CSS px per layer
		const step = Math.max(1, Math.round(stepCSS * dpr)); // device px
		const stepJitterCSS = (OPTIONS as any).stepJitter ?? 1.0;
		const cx = Math.round((this.width * dpr) / 2);

		// font
		const weight = (OPTIONS as any).fontWeight ? `${(OPTIONS as any).fontWeight} ` : '';
		const fontSpec = `${weight}${this.fontPx}px ${OPTIONS.fontFamily}`;
		ctx.font = fontSpec;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';

		// parent drift (whole stack moves together)
		const t = scrollYcss * OPTIONS.parentNoiseFreq;
		const pdx = Math.round(this.noise.noise1(t) * OPTIONS.parentAmpX * dpr);
		const pdy = Math.round(this.noise.noise1(t + 123.456) * OPTIONS.parentAmpY * dpr);

		ctx.save();
		ctx.translate(pdx, pdy);

		// how many ghost layers are "enabled"
		const full = Math.max(0, Math.min(OPTIONS.maxLayers, Math.floor((baseYcss - this.startY) / stepCSS)));

		// draw ghosts back → front
		for (let i = full; i >= 1; i--) {
			const jitterCSS = this.noise.noise1(4000 + i * 0.73) * stepJitterCSS;
			const y = baseY - i * step - Math.round(jitterCSS * dpr);

			const phase = i * 0.13;
			const nX = this.noise.noise2(phase, t);
			const dx = Math.round((nX * OPTIONS.childExtraAmpX + (this.randX[i] || 0)) * dpr);

			const baseA = this.randA[i] ?? 0.3;
			const a = baseA * fade; // fade whole stack

			if (a <= 0) continue;

			ctx.save();
			ctx.globalAlpha = a;
			ctx.translate(dx, 0);
			ctx.fillStyle = '#fff';
			ctx.fillText(OPTIONS.text, cx, y);
			ctx.restore();
		}

		// lead on top
		if (fade > 0) {
			ctx.globalAlpha = fade;
			ctx.fillStyle = '#fff';
			ctx.fillText(OPTIONS.text, cx, baseY);
		}

		ctx.restore();

		// reset
		ctx.globalAlpha = 1;
		ctx.globalCompositeOperation = 'source-over';
		ctx.setTransform(1, 0, 0, 1, 0, 0);
	}

	private getScrollY(): number {
		// Try existing OverlayScrollbars body instance first
		try {
			const os = (OverlayScrollbars as any)(document.body);
			if (os && typeof os.elements === 'function') {
				const els = os.elements();
				const scrollEl: any = els.scrollOffsetElement || els.viewport;
				if (scrollEl && typeof scrollEl.scrollTop === 'number') {
					return scrollEl.scrollTop;
				}
			}
		} catch {
			// ignore – fall back to native
		}

		// native window scroll as fallback
		return window.scrollY || window.pageYOffset || 0;
	}

	private computeFade(progress: number): number {
		const start = (OPTIONS as any).fadeOutStart ?? 0.7;
		const end = (OPTIONS as any).fadeOutEnd ?? 1.0;

		if (end <= start) return 1;
		if (progress <= start) return 1;
		if (progress >= end) return 0;

		const t = (progress - start) / (end - start);
		return 1 - t; // linear 1 → 0
	}

	// ---- randomness (stable) ------------------------------------------------
	private precomputeRandoms(): void {
		let s = this.hash32(OPTIONS.text);
		const rnd = () => {
			s |= 0;
			s = (s + 0x6d2b79f5) | 0;
			let t = Math.imul(s ^ (s >>> 15), 1 | s);
			t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
			return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
		};

		this.randX.length = 0;
		this.randA.length = 0;
		for (let i = 1; i <= OPTIONS.maxLayers; i++) {
			const rX = rnd();
			const rA = rnd();
			const dx = -OPTIONS.jitterMax + rX * (OPTIONS.jitterMax + OPTIONS.jitterMax);
			const a = OPTIONS.opacityMin + rA * (OPTIONS.opacityMax - OPTIONS.opacityMin);
			this.randX[i] = Math.round(dx);
			this.randA[i] = Math.max(0, Math.min(1, a));
		}
	}

	private hash32(str: string): number {
		let h = 2166136261 >>> 0;
		for (let i = 0; i < str.length; i++) {
			h ^= str.charCodeAt(i);
			h = Math.imul(h, 16777619);
		}
		return h >>> 0;
	}

	private fontPxCSSFromWidth(w: number): number {
		const rules = OPTIONS.fontPxBreakpoints || [];
		for (const r of rules) if (w < r.max) return r.px;
		return rules.length ? rules[rules.length - 1].px : 64;
	}
}

class Perlin {
	private p = new Uint8Array(512);
	constructor(seed = 1337) {
		const perm = new Uint8Array(256);
		for (let i = 0; i < 256; i++) perm[i] = i;
		let s = seed >>> 0;
		const rnd = () => (s = (s * 1664525 + 1013904223) >>> 0) / 0xffffffff;
		for (let i = 255; i > 0; i--) {
			const j = (rnd() * (i + 1)) | 0;
			[perm[i], perm[j]] = [perm[j], perm[i]];
		}
		for (let i = 0; i < 512; i++) this.p[i] = perm[i & 255];
	}
	private fade(t: number) {
		return t * t * t * (t * (t * 6 - 15) + 10);
	}
	private lerp(a: number, b: number, t: number) {
		return a + t * (b - a);
	}
	private grad1(h: number, x: number) {
		return h & 1 ? -x : x;
	}
	noise1(x: number) {
		const X = Math.floor(x) & 255,
			xf = x - Math.floor(x),
			u = this.fade(xf);
		const a = this.p[X],
			b = this.p[X + 1];
		return this.lerp(this.grad1(a, xf), this.grad1(b, xf - 1), u);
	}
	noise2(x: number, y: number) {
		const X = Math.floor(x) & 255,
			Y = Math.floor(y) & 255;
		const xf = x - Math.floor(x),
			yf = y - Math.floor(y);
		const u = this.fade(xf),
			v = this.fade(yf);
		const p = this.p;
		const aa = p[X + p[Y]],
			ab = p[X + p[Y + 1]];
		const ba = p[X + 1 + p[Y]],
			bb = p[X + 1 + p[Y + 1]];
		const g = (h: number, x: number, y: number) => (h & 2 ? -x : x) + (h & 1 ? -y : y);
		const x1 = this.lerp(g(aa, xf, yf), g(ba, xf - 1, yf), u);
		const x2 = this.lerp(g(ab, xf, yf - 1), g(bb, xf - 1, yf - 1), u);
		return this.lerp(x1, x2, v);
	}
}
